import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { setStripeKey } from '../stores/paymentStore';
import { fetchBoard } from '../services/api';
import {
    trackPageView, trackAddToCart, trackCompleteRegistration,
    trackScreenPresented, trackScreenCompleted,
    trackResetOnFirstScreen,
} from '../services/analytics';
import { setRinoEventContext } from '../services/rinoAnalytics';
import { usePersistence } from '../hooks/usePersistence';
import { useNavigation } from '../hooks/useNavigation';
import { useValidation } from '../hooks/useValidation';

const OnboardingContext = createContext();

export const useOnboarding = () => {
    const context = useContext(OnboardingContext);
    if (!context) {
        throw new Error('useOnboarding must be used within OnboardingProvider');
    }
    return context;
};

function getOnboardingId() {
    const path = window.location.pathname;
    const match = path.match(/\/(.*?)\/?$/);
    return match ? match[1] : 'quiz';
}

function extractImageUrls(data) {
    const imageUrls = new Set();
    const findImages = (obj) => {
        if (Array.isArray(obj)) {
            obj.forEach(item => findImages(item));
        } else if (obj && typeof obj === 'object') {
            if (obj.type === 'image' && obj.url) {
                imageUrls.add(obj.url);
            }
            Object.values(obj).forEach(value => findImages(value));
        }
    };
    if (data.header) findImages(data.header);
    if (data.groups) findImages(data.groups);
    return Array.from(imageUrls);
}

export const OnboardingProvider = ({ children }) => {
    const [data, setData] = useState(null);
    const [preloadedImages, setPreloadedImages] = useState(new Set());
    const [preloadingComplete, setPreloadingComplete] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const persistence = usePersistence();
    const {
        formData, globalAnswers, globalAnswersRef,
        userID, selectedPlan, urlParams, buttonLoadingStates,
        loadGlobalAnswersFromStorage,
        updateGlobalAnswer, updateFormData,
        updateSelectedPlan, setButtonLoading,
        resetSession, clearAllData,
    } = persistence;

    const validation = useValidation();
    const {
        isCurrentScreenValid,
        isValidEmail,
        normalizeEmailInput,
        hasRequiredFields,
        validateRequiredFields,
        showValidationError,
        setScreenValidation,
    } = validation;

    const navigation = useNavigation({ data });
    const {
        currentGroupIndex, currentScreenIndex,
        getCurrentScreen, getTotalScreens, getCurrentScreenPosition,
        updateURL, initializeFromURL,
        goToPreviousScreen, goToScreenWithName,
    } = navigation;

    const replaceTemplates = useCallback((text) => {
        if (!text || typeof text !== 'string') return text;
        return text.replace(/\{\{([^}]+)\}\}/g, (match, key) => {
            const answer = globalAnswers.get(key);
            return answer && answer.text ? answer.text : match;
        });
    }, [globalAnswers]);

    const preloadImages = async (boardData) => {
        const imageUrls = extractImageUrls(boardData);
        console.log(`Starting preload of ${imageUrls.length} images...`);

        const loaded = new Set();
        const preloadPromises = imageUrls.map(url =>
            new Promise((resolve) => {
                const img = new Image();
                img.onload = () => { loaded.add(url); resolve(); };
                img.onerror = () => resolve();
                img.src = url;
            })
        );

        await Promise.all(preloadPromises);
        setPreloadedImages(loaded);
        setPreloadingComplete(true);
    };

    // Board loading
    useEffect(() => {
        const loadData = async () => {
            try {
                const onboardingId = getOnboardingId();
                const jsonData = await fetchBoard(onboardingId);
                setData(jsonData);

                if (jsonData.system.isStripeEnabled !== false) {
                    setStripeKey(jsonData.system.stripePublishableKey);
                }

                loadGlobalAnswersFromStorage();
                initializeFromURL(jsonData);
                setTimeout(() => {
                    preloadImages(jsonData);
                }, 2000);
                setLoading(false);
            } catch (err) {
                console.error('Failed to load onboarding data:', err);
                setError(err.message);
                setLoading(false);
            }
        };

        loadData();

    }, []);

    const screenWillCompleteHandler = useCallback((currentScreen) => {
        const firstScreenName = data?.groups?.[0]?.screens?.[0]?.name;

        if (currentScreen.name === firstScreenName) {
            trackAddToCart();
        }

        if (currentScreen.name == 'email') {
            const email = formData.get('email');
            const fbc = urlParams.fbclid;
            trackCompleteRegistration(email, fbc);
        }
    }, [data, formData, urlParams]);

    const goToNextScreen = useCallback((skipValidation = false) => {
        const currentScreen = getCurrentScreen();

        if (!skipValidation && currentScreen && hasRequiredFields(currentScreen)) {
            if (!validateRequiredFields(currentScreen, formData)) {
                showValidationError(currentScreen, formData);
                return;
            }
        }

        if (currentScreen?.name && currentScreen.elements?.some(el => el.type === 'specialOffer')) {
            const prev = globalAnswersRef.current.get(currentScreen.name);
            if (prev?.value !== 'purchased') {
                updateGlobalAnswer(currentScreen.name, {
                    text: 'notPurchased',
                    value: 'notPurchased',
                    elementName: 'specialOffer',
                });
            }
        }

        screenWillCompleteHandler(currentScreen);

        if (currentScreen && currentScreen.name) {
            const answer = globalAnswersRef.current.get(currentScreen.name);
            const reply = answer?.value ?? null;
            trackScreenCompleted({
                screenCid: currentScreen.name,
                onboardingId: getOnboardingId(),
                reply,
            });
        }

        navigation.goToNextScreen({
            skipValidation: true,
            globalAnswersRef,
            formData,
            hasRequiredFields,
            validateRequiredFields,
            showValidationError,
        });
    }, [
        getCurrentScreen, formData, globalAnswersRef, updateGlobalAnswer,
        hasRequiredFields, validateRequiredFields, showValidationError,
        screenWillCompleteHandler, navigation,
    ]);

    const screenDidPresentedHandler = useCallback((currentScreen) => {
        const firstScreenName = data?.groups?.[0]?.screens?.[0]?.name;

        if (currentScreen.name === firstScreenName) {
            trackResetOnFirstScreen({
                screenCid: currentScreen.name,
                onboardingId: getOnboardingId(),
            });

            resetSession();

            if (urlParams.clear) {
                clearAllData();
            }

            if (urlParams.email) {
                updateGlobalAnswer('email', {
                    text: urlParams.email,
                    value: urlParams.email,
                    elementName: 'email'
                });
                updateFormData('email', urlParams.email);
            }
        }
    }, [data, urlParams, resetSession, clearAllData, updateGlobalAnswer, updateFormData]);

    const lastTrackedScreenRef = useRef(null);

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }, [currentGroupIndex, currentScreenIndex]);

    useEffect(() => {
        const ctx = {
            engine: 'rino',
            onboardingID: getOnboardingId(),
            goal: globalAnswers.get('goal')?.value,
            voice: globalAnswers.get('voice')?.value,
        };
        for (const [k, v] of Object.entries(urlParams || {})) {
            ctx[`query_${k}`] = v;
        }
        setRinoEventContext(ctx);
    }, [urlParams, globalAnswers]);

    // Screen change effects
    useEffect(() => {
        if (data && !loading) {
            const currentScreen = getCurrentScreen();
            if (!currentScreen || !currentScreen.name) return;

            const screenKey = `${currentGroupIndex}-${currentScreenIndex}`;
            if (lastTrackedScreenRef.current === screenKey) return;
            lastTrackedScreenRef.current = screenKey;

            updateURL();
            setScreenValidation(true);
            screenDidPresentedHandler(currentScreen);

            trackPageView();

            trackScreenPresented({
                screenCid: currentScreen.name,
                onboardingId: getOnboardingId(),
            });
        }
    }, [data, currentGroupIndex, currentScreenIndex, loading]);

    const value = {
        data,
        currentGroupIndex,
        currentScreenIndex,
        formData,
        globalAnswers,
        loading,
        error,
        preloadedImages,
        preloadingComplete,
        isCurrentScreenValid,
        userID,
        selectedPlan,
        urlParams,
        buttonLoadingStates,
        onboardingId: getOnboardingId(),
        getCurrentScreen,
        getTotalScreens,
        getCurrentScreenPosition,
        goToNextScreen,
        goToPreviousScreen,
        goToScreenWithName,
        replaceTemplates,
        updateGlobalAnswer,
        updateFormData,
        isValidEmail,
        normalizeEmailInput,
        setScreenValidation,
        updateSelectedPlan,
        setButtonLoading,
    };

    return (
        <OnboardingContext.Provider value={value}>
            {children}
        </OnboardingContext.Provider>
    );
};
